import { useState, useEffect } from 'react'
import { transcriptsApi, projectsApi } from '../api/client'
import { format } from 'date-fns'
import { HiUpload, HiDocumentText, HiPlay, HiCheckCircle, HiClock, HiX, HiPlus, HiSparkles } from 'react-icons/hi'

function TranscriptCard({ transcript, onProcess, onProcessAndCreate }) {
  const [showProjectSelect, setShowProjectSelect] = useState(false)
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [newProjectName, setNewProjectName] = useState('')
  const [creating, setCreating] = useState(false)
  
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    processed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  }

  async function loadProjects() {
    if (projects.length > 0) return
    setLoadingProjects(true)
    try {
      const response = await projectsApi.list()
      setProjects(response.data)
    } catch (err) {
      console.error('Failed to load projects:', err)
    } finally {
      setLoadingProjects(false)
    }
  }

  async function handleProcessAndCreate() {
    if (!selectedProjectId && !newProjectName) return
    setCreating(true)
    try {
      await onProcessAndCreate(transcript.id, {
        projectId: selectedProjectId !== 'new' ? selectedProjectId : null,
        newProjectName: selectedProjectId === 'new' ? newProjectName : null
      })
      setShowProjectSelect(false)
      setSelectedProjectId('')
      setNewProjectName('')
    } finally {
      setCreating(false)
    }
  }

  // Derive status from processed boolean
  const status = transcript.processed ? 'processed' : 'pending'

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-surface-100 dark:bg-surface-700 rounded-lg">
            <HiDocumentText className="w-6 h-6 text-surface-600 dark:text-surface-400" />
          </div>
          <div>
            <h3 className="font-medium text-surface-800 dark:text-surface-200">{transcript.title}</h3>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
              Uploaded {format(new Date(transcript.created_at), 'PPP')}
            </p>
          </div>
        </div>
        
        <span className={`text-xs px-2 py-1 rounded ${statusColors[status]}`}>
          {status}
        </span>
      </div>

      {/* Extracted Action Items */}
      {transcript.extracted_actions && transcript.extracted_actions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-surface-200 dark:border-surface-700">
          <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">Extracted Items:</h4>
          <ul className="space-y-2">
            {transcript.extracted_actions.slice(0, 5).map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <HiCheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span className="text-surface-600 dark:text-surface-400">{item.description || item.title || item}</span>
              </li>
            ))}
            {transcript.extracted_actions.length > 5 && (
              <li className="text-sm text-surface-500 dark:text-surface-400">
                +{transcript.extracted_actions.length - 5} more items
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Process Buttons */}
      {!transcript.processed && (
        <div className="mt-4 pt-4 border-t border-surface-200 dark:border-surface-700 space-y-3">
          {showProjectSelect ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
                Select Target Project
              </label>
              <select
                value={selectedProjectId}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value)
                  if (e.target.value !== 'new') setNewProjectName('')
                }}
                onFocus={loadProjects}
                className="input"
              >
                <option value="">Choose a project...</option>
                {loadingProjects ? (
                  <option disabled>Loading...</option>
                ) : (
                  projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))
                )}
                <option value="new">+ Create new project</option>
              </select>
              
              {selectedProjectId === 'new' && (
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="New project name..."
                  className="input"
                  autoFocus
                />
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowProjectSelect(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProcessAndCreate}
                  disabled={(!selectedProjectId || (selectedProjectId === 'new' && !newProjectName.trim())) || creating}
                  className="btn btn-primary flex-1"
                >
                  {creating ? 'Creating...' : 'Create Tasks'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => onProcess(transcript.id)}
                className="btn btn-secondary flex-1"
              >
                <HiPlay className="w-4 h-4 mr-2" />
                Extract Only
              </button>
              <button
                onClick={() => setShowProjectSelect(true)}
                className="btn btn-primary flex-1"
              >
                <HiSparkles className="w-4 h-4 mr-2" />
                Create Tasks
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function UploadModal({ onClose, onUpload, onProcessAndCreate }) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [meetingDate, setMeetingDate] = useState('')
  const [source, setSource] = useState('')
  const [file, setFile] = useState(null)
  const [uploadMode, setUploadMode] = useState('paste') // 'paste' or 'file'
  const [loading, setLoading] = useState(false)
  
  // Project selection for immediate processing
  const [processOnUpload, setProcessOnUpload] = useState(false)
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [newProjectName, setNewProjectName] = useState('')
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)

  async function loadProjects() {
    if (projects.length > 0) return
    setLoadingProjects(true)
    try {
      const response = await projectsApi.list()
      setProjects(response.data)
    } catch (err) {
      console.error('Failed to load projects:', err)
    } finally {
      setLoadingProjects(false)
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      // Auto-fill title from filename if empty
      if (!title) {
        const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '')
        setTitle(nameWithoutExt)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim()) return
    if (uploadMode === 'paste' && !content.trim()) return
    if (uploadMode === 'file' && !file) return
    
    // Validate project selection if processing
    if (processOnUpload && !selectedProjectId && !newProjectName) return

    setLoading(true)
    try {
      // First upload the transcript
      let uploadedTranscript
      if (uploadMode === 'file') {
        uploadedTranscript = await onUpload({ file, title: title.trim() }, true)
      } else {
        uploadedTranscript = await onUpload({
          title: title.trim(),
          raw_text: content.trim(),
          meeting_date: meetingDate || null,
          source: source.trim() || null,
        }, false)
      }
      
      // If process on upload is enabled, call process and create
      if (processOnUpload && uploadedTranscript?.id) {
        await onProcessAndCreate(uploadedTranscript.id, {
          projectId: selectedProjectId !== 'new' ? selectedProjectId : null,
          newProjectName: selectedProjectId === 'new' ? newProjectName : null
        })
      }
      
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-surface-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700 sticky top-0 bg-white dark:bg-surface-800">
          <h2 className="text-lg font-semibold text-surface-800 dark:text-surface-200">Upload Transcript</h2>
          <button
            onClick={onClose}
            className="p-1 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 rounded"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Upload Mode Tabs */}
          <div className="flex border border-surface-200 dark:border-surface-700 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setUploadMode('paste')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                uploadMode === 'paste' 
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 border-r border-brand-200' 
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-700 border-r border-surface-200 dark:border-surface-700'
              }`}
            >
              Paste Text
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                uploadMode === 'file' 
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' 
                  : 'text-surface-600 dark:text-surface-400 hover:bg-surface-50 dark:hover:bg-surface-700'
              }`}
            >
              Upload File
            </button>
          </div>

          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Weekly Team Standup"
              className="input"
            />
          </div>

          {uploadMode === 'file' ? (
            <div>
              <label className="label">Transcript File *</label>
              <div className="border-2 border-dashed border-surface-300 dark:border-surface-600 rounded-lg p-6 text-center">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <HiDocumentText className="w-6 h-6 text-brand-500" />
                    <span className="font-medium text-surface-700 dark:text-surface-300">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="p-1 text-surface-400 dark:text-surface-500 hover:text-red-500"
                    >
                      <HiX className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <HiUpload className="w-8 h-8 text-surface-400 dark:text-surface-500 mx-auto mb-2" />
                    <p className="text-sm text-surface-600 dark:text-surface-400 mb-2">
                      Drag and drop or click to select a file
                    </p>
                    <p className="text-xs text-surface-500 dark:text-surface-400">
                      Supported: .txt, .vtt, .srt, .docx
                    </p>
                  </>
                )}
                <input
                  type="file"
                  accept=".txt,.vtt,.srt,.doc,.docx"
                  onChange={handleFileChange}
                  className={file ? 'hidden' : 'absolute inset-0 w-full h-full opacity-0 cursor-pointer'}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Meeting Date</label>
                  <input
                    type="datetime-local"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Source</label>
                  <input
                    type="text"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="e.g., Teams, Zoom"
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="label">Transcript Content *</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your meeting transcript here..."
                  rows={10}
                  className="input resize-none font-mono text-sm"
                />
              </div>
            </>
          )}

          {/* Process with AI option */}
          <div className="border-t border-surface-200 dark:border-surface-700 pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={processOnUpload}
                onChange={(e) => {
                  setProcessOnUpload(e.target.checked)
                  if (e.target.checked) loadProjects()
                }}
                className="w-4 h-4 rounded border-surface-300 dark:border-surface-600 text-brand-600 focus:ring-brand-500"
              />
              <div>
                <span className="font-medium text-surface-800 dark:text-surface-200 flex items-center gap-2">
                  <HiSparkles className="w-4 h-4 text-brand-500" />
                  Process with AI and create tasks
                </span>
                <p className="text-sm text-surface-500 dark:text-surface-400">
                  Automatically extract action items and create tasks in a project
                </p>
              </div>
            </label>
            
            {processOnUpload && (
              <div className="mt-4 space-y-3 pl-7">
                <div>
                  <label className="label">Target Project *</label>
                  <select
                    value={selectedProjectId}
                    onChange={(e) => {
                      setSelectedProjectId(e.target.value)
                      if (e.target.value !== 'new') setNewProjectName('')
                    }}
                    className="input"
                  >
                    <option value="">Choose a project...</option>
                    {loadingProjects ? (
                      <option disabled>Loading...</option>
                    ) : (
                      projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))
                    )}
                    <option value="new">+ Create new project</option>
                  </select>
                </div>
                
                {selectedProjectId === 'new' && (
                  <div>
                    <label className="label">New Project Name *</label>
                    <input
                      type="text"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder="Enter project name..."
                      className="input"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !title.trim() || 
                (uploadMode === 'paste' && !content.trim()) || 
                (uploadMode === 'file' && !file) || 
                (processOnUpload && !selectedProjectId) ||
                (processOnUpload && selectedProjectId === 'new' && !newProjectName.trim()) ||
                loading
              }
              className="btn btn-primary"
            >
              {loading ? (
                processOnUpload ? 'Processing...' : 'Uploading...'
              ) : (
                processOnUpload ? 'Upload & Create Tasks' : 'Upload Transcript'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function TranscriptsPage() {
  const [transcripts, setTranscripts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [processing, setProcessing] = useState(null)

  useEffect(() => {
    fetchTranscripts()
  }, [])

  async function fetchTranscripts() {
    try {
      setLoading(true)
      const response = await transcriptsApi.list()
      setTranscripts(response.data)
    } catch (err) {
      console.error('Failed to fetch transcripts:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpload(data, isFile = false) {
    let response
    if (isFile) {
      response = await transcriptsApi.uploadFile(data.file, data.title)
    } else {
      response = await transcriptsApi.upload(data)
    }
    setTranscripts(prev => [response.data, ...prev])
    return response.data // Return the uploaded transcript for processing
  }

  async function handleProcess(transcriptId) {
    setProcessing(transcriptId)
    try {
      const response = await transcriptsApi.process(transcriptId)
      // Refresh transcript to get updated data
      fetchTranscripts()
    } catch (err) {
      console.error('Failed to process transcript:', err)
    } finally {
      setProcessing(null)
    }
  }

  async function handleProcessAndCreate(transcriptId, { projectId, newProjectName }) {
    setProcessing(transcriptId)
    try {
      const response = await transcriptsApi.processAndCreate(transcriptId, {
        projectId,
        newProjectName,
        createTasks: true
      })
      // Refresh transcripts to show updated status
      fetchTranscripts()
      // Show success feedback
      const tasksCount = response.data.tasks_created?.length || 0
      const projectName = response.data.project_name
      const isNew = response.data.created_project
      alert(`Successfully created ${tasksCount} task(s) in ${isNew ? 'new ' : ''}project: ${projectName}`)
    } catch (err) {
      console.error('Failed to process and create tasks:', err)
      alert('Failed to process transcript and create tasks')
    } finally {
      setProcessing(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">Meeting Transcripts</h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Upload meeting transcripts for AI to extract action items
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn btn-primary"
        >
          <HiUpload className="w-5 h-5 mr-2" />
          Upload Transcript
        </button>
      </div>

      {/* Transcripts List */}
      {transcripts.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {transcripts.map(transcript => (
            <TranscriptCard
              key={transcript.id}
              transcript={transcript}
              onProcess={handleProcess}
              onProcessAndCreate={handleProcessAndCreate}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-surface-100 dark:bg-surface-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiDocumentText className="w-8 h-8 text-surface-400 dark:text-surface-500" />
          </div>
          <h3 className="text-lg font-medium text-surface-800 dark:text-surface-200 mb-2">No transcripts yet</h3>
          <p className="text-surface-500 dark:text-surface-400 mb-4">
            Upload a meeting transcript to extract action items automatically
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="btn btn-primary"
          >
            <HiUpload className="w-5 h-5 mr-2" />
            Upload Transcript
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
          onProcessAndCreate={handleProcessAndCreate}
        />
      )}
    </div>
  )
}
